import classNames from 'classnames';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, MouseEvent, useCallback } from 'react';
import { AvailabilityResourceType, ConflictReason } from '../../../stores/DayAvailabilityStore';
import { getDayOfWeekAsString } from '../../../utils/dateCalculations';
import { getCardTextColor } from '../../../utils/getCardTextColor';
import {
  availabilityResourceTypeToWarningType,
  getWarningTypeTranslation,
} from '../../../utils/getWarningTypeTranslation';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { ForeignTripCard } from '../ForeignTripCard/ForeignTripCard';
import { GridCard } from '../GridCard/GridCard';
import { GridContext, GridDateType, RowItem, TeachingBlockCardType } from '../types';
import { VirtualGridCard } from '../VirtualGridCard/VirtualGridCard';
import styles from './GridWeek.module.scss';

type GridWeekProps = {
  row: RowItem;
  weekNumber: number;
  year: number;
  days: GridDateType[];
};

type DayAvailability = GridDateType & {
  valid: boolean;
  blocked: boolean;
  maybe: boolean;
  placed: boolean;
  /** Dauerhafte Nichtverfügbarkeit der Zeilen-Entität — unabhängig vom Picken. */
  rowBlocked?: boolean;
  rowMaybe?: boolean;
  /** Grund der dauerhaften Nichtverfügbarkeit (Tooltip innerhalb der Zelle). */
  rowHint?: string;
  /** Menschenlesbarer Grund für ein rotes/gelbes Feld (Hover-Tooltip beim Picken). */
  hint?: string;
  /** Raum-Kontext: Zelle außerhalb der (feststehenden) Zeit-Spalten der Karte → ausgegraut. */
  dimmed?: boolean;
};

/** Ab so vielen nicht verfügbaren Tagen wird die ganze Woche mit einem Zeichen markiert. */
const MIN_MARKED_DAYS = 3;

/** Zeilen-Kontext des Boards → Ressourcentyp der Zeilen-Entität. */
const gridContextToResourceType = (context: GridContext): AvailabilityResourceType => {
  switch (context) {
    case 'teachers':
      return 'teacher';
    case 'rooms':
      return 'room';
    case 'subjects':
      return 'subject';
    default:
      return 'class';
  }
};

export const GridWeek: FC<GridWeekProps> = observer(({ row, weekNumber, year, days }) => {
  const store = useTeachingBlockStore();

  const availabilityStore = store.getAvailabilityStore();

  // "Verfügbarkeiten und Konflikte anzeigen" schaltet die komplette Zell-Einfärbung ab —
  // auch die grüne Drop-Rückmeldung, damit beim Ausschalten nichts davon übrig bleibt.
  const showAvailability = store.highlightMode.includes('warnings');

  // Perf (Fix C, Vorbild GridCell): `pickedCard` nur in den Zeilen der gepickten
  // Karte abonnieren. Alle anderen `GridWeek`s lesen `pickedCard` nicht und rendern
  // beim Picken daher NICHT neu (statt aller rows × weeks Zellen).
  const isHighlightRow = store.rowsToHighlight.has(row.value);
  const pickedCard = isHighlightRow ? store.pickedCard : null;

  // Perf (Fix B): O(1)-Lookup über vorberechnete Row-Week-Indizes statt O(n)-Filter
  // der vollen Karten-Arrays. Die gepickte Karte wird nur in Highlight-Zeilen
  // ausgeblendet (dort liegt sie); in anderen Zeilen ist sie ohnehin nicht enthalten.
  const indexKey = `${row.value}:${weekNumber}:${year}`;
  // Per-Key fein-granular (comparer.shallow): diese GridWeek rendert beim Ablegen nur
  // neu, wenn sich IHRE Karten-Liste ändert – nicht bei jeder placedCards-Mutation.
  // Aufgenommene Karte aus dem Grid nehmen ("in der Hand"). Im Raum-Kontext gilt das nur für
  // den Quell-Raum (Verschiebemodus) – im Kopiermodus bleibt die Karte in allen ihren Räumen
  // sichtbar, weil dort Kopien gesetzt werden.
  const shouldHidePicked =
    pickedCard != null && (store.gridContext !== 'rooms' || (!store.pickIsCopy && row.value === store.pickedFromRoom));
  const virtualCardsInWeek = store.cardStore
    .virtualCardsForRowWeek(indexKey)
    .filter((card) => !(shouldHidePicked && pickedCard?.card.uuid === card.parentUuid));
  const placedCardsInWeek = store.cardStore
    .placedCardsForRowWeek(indexKey)
    .filter((card) => !(shouldHidePicked && pickedCard?.card.uuid === card.uuid));

  // Inline-Berechnung im observer (Vorbild: TimetableGrid/GridCell): rechnet bei
  // jeder Observable-Änderung neu und liest den (in 7.2a konsistent gehaltenen)
  // Availability-Store frisch aus. `pickedCard` ist nur in Highlight-Zeilen gesetzt
  // (= Zeilen, in denen die Karte vorkommt) – exakt dort soll die Markierung greifen.
  const availabilityCard = pickedCard ? store.getAvailabilityCardForPickedCard() : undefined;
  const placeholderFilter = pickedCard ? store.conflictStore.getPlaceholderFilterForCard(pickedCard.card) : undefined;

  // Raum-Kontext: die aufgenommene Karte ist zeitlich fixiert, gesucht wird nur der Raum.
  // Ablegbar (grün) sind daher genau die eigenen Zeit-Spalten der Karte – in jeder Raum-Zeile;
  // alle übrigen Spalten werden ausgegraut. Unabhängig vom "Verfügbarkeiten"-Toggle.
  const pickedInRooms = !!pickedCard && store.gridContext === 'rooms';
  const pickedRangeStart = pickedInRooms ? dayjs(pickedCard?.card.startDate).startOf('day') : null;
  const pickedRangeEnd = pickedInRooms ? dayjs(pickedCard?.card.endDate).startOf('day') : null;

  // Ressourcen-Name für den Konflikt-Grund. Bei `divisionGroup` ist `resourceUuid` die Klassen-UUID.
  const resourceName = (reason: ConflictReason): string => {
    switch (reason.type) {
      case 'teacher': {
        const teacher = store.teachers.get(reason.resourceUuid);
        return teacher?.displayNameShort ?? teacher?.shortName ?? '';
      }
      case 'class':
      case 'divisionGroup':
        return store.classes.get(reason.resourceUuid)?.shortName ?? '';
      case 'room':
        return store.rooms.get(reason.resourceUuid)?.name ?? '';
      default:
        return '';
    }
  };

  // Baut aus den Konflikt-Gründen einen Tooltip-Text: "<Grund>: <Ressource>", dedupliziert.
  const buildHint = (reasons: ConflictReason[] | undefined): string | undefined => {
    if (!reasons || reasons.length === 0) return undefined;
    const parts = reasons.map((reason) => {
      const label = getWarningTypeTranslation(
        availabilityResourceTypeToWarningType(reason.type, reason.kind === 'blocked', reason.kind === 'maybe'),
      );
      const name = resourceName(reason);
      return name ? `${label}: ${name}` : label;
    });
    return [...new Set(parts)].join(' | ') || undefined;
  };

  const availabilityDays: DayAvailability[] = days.map((day) => {
    // Dauerhafte Nichtverfügbarkeit der Zeilen-Entität — auch mit Karte in der Hand
    // berechnet, damit das Wochen-Zeichen beim Ziehen nicht verschwindet.
    // `row.value` ist je nach gridContext die UUID der Klasse/Lehrkraft/des Raums.
    const date = dayjs(day.dateString).startOf('day').toDate();
    const rowBlocked = availabilityStore?.isResourceBlocked(date, row.value) ?? false;
    const rowMaybe = !rowBlocked && (availabilityStore?.isResourceMaybe(date, row.value) ?? false);

    // Raum-Kontext: nur die eigenen Zeit-Spalten der Karte sind ablegbar, der Rest ausgegraut.
    if (pickedInRooms) {
      const d = dayjs(day.dateString).startOf('day');
      const inRange =
        !day.holidayUuid &&
        !!pickedRangeStart &&
        !!pickedRangeEnd &&
        d.isSameOrAfter(pickedRangeStart, 'day') &&
        d.isSameOrBefore(pickedRangeEnd, 'day');
      return {
        ...day,
        valid: inRange,
        blocked: false,
        maybe: false,
        placed: false,
        dimmed: !inRange && !day.holidayUuid,
        rowBlocked,
        rowMaybe,
      };
    }

    if (pickedCard && availabilityStore && availabilityCard) {
      const { status, reasons } = availabilityStore.checkCardAvailability(
        dayjs(day.dateString).startOf('day').toDate(),
        availabilityCard,
        'classes',
        { shouldCountPlacement: placeholderFilter },
      );
      const isConflict = status === 'blocked' || status === 'maybe' || status === 'placed';
      return {
        ...day,
        valid: !isConflict,
        blocked: status === 'blocked',
        maybe: status === 'maybe',
        placed: status === 'placed',
        rowBlocked,
        rowMaybe,
        hint: isConflict ? buildHint(reasons) : undefined,
      };
    }

    return {
      ...day,
      valid: false,
      blocked: false,
      maybe: false,
      placed: false,
      rowBlocked,
      rowMaybe,
      // Eigenes Feld statt `hint`: der Pick-Tooltip sitzt oberhalb der Zelle und wird
      // dort vom `overflow: hidden` des Boards abgeschnitten. Dieser hier wird innerhalb
      // der Zelle positioniert (wie der Datums-Tooltip) und ist damit immer sichtbar.
      rowHint:
        rowBlocked || rowMaybe
          ? getWarningTypeTranslation(
              availabilityResourceTypeToWarningType(gridContextToResourceType(store.gridContext), rowBlocked, rowMaybe),
            )
          : undefined,
    };
  });

  // Überwiegend nicht verfügbare Wochen bekommen EIN Zeichen über die ganze Woche
  // statt einer Markierung je Tageszelle — in den schmalen Tagesstreifen ist dafür
  // kein Platz. Ferientage zählen nicht mit.
  const blockedDays = availabilityDays.filter((day) => day.rowBlocked && !day.holidayUuid).length;
  const restrictedDays = availabilityDays.filter((day) => (day.rowBlocked || day.rowMaybe) && !day.holidayUuid).length;
  const weekBlocked = showAvailability && blockedDays >= MIN_MARKED_DAYS;
  const weekMaybe = showAvailability && !weekBlocked && restrictedDays >= MIN_MARKED_DAYS;

  const getDayTitle = (dateString: string) => {
    const day = dayjs(dateString).day();
    const title = dayjs(dateString).format('DD.MM.YYYY');
    if (day === 0 || day === 6) {
      const dayOfWeek = getDayOfWeekAsString(dateString);
      return `${title} (${dayOfWeek})`;
    }

    return title;
  };

  // Stabiler Handler (eine Referenz für alle Karten): GridCard reicht die Karte als
  // 2. Argument zurück, daher braucht es keine per-Karte-Closure. So bleibt das `memo`
  // der GridCards intakt – ein GridWeek-Rerender rendert die Karten darin NICHT mit.
  const handleCardPick = useCallback(
    (event: MouseEvent<HTMLDivElement>, card: TeachingBlockCardType) => {
      // Raum-Kontext: den Raum (die Zeile) merken, aus dem die Karte kommt (Verschiebemodus);
      // mit gedrückter Shift-Taste in den Kopiermodus (Karte in mehrere Räume setzen).
      store.pickCard(
        card,
        { x: event.pageX, y: event.pageY },
        false,
        store.gridContext === 'rooms' ? row.value : undefined,
        event.shiftKey,
      );
    },
    [store, row.value],
  );

  return (
    <div
      className={classNames(styles['grid-week'], {
        [styles['week-blocked']]: weekBlocked,
        [styles['week-maybe']]: weekMaybe,
      })}
    >
      {availabilityDays.map(
        (
          { valid, blocked, maybe, placed, rowBlocked, rowMaybe, rowHint, holidayUuid, dateString, hint, dimmed },
          dayIndex,
        ) => {
          // Rangfolge: gesperrt > eingeschränkt > ablegbar. Grün heißt damit "wirklich
          // konfliktfrei". Gesperrt/belegt/eingeschränkt sieht beim Picken genauso aus wie
          // die dauerhafte Nichtverfügbarkeit — eine Bildsprache statt eigener Drag-Farben.
          const dayBlocked = showAvailability && !holidayUuid && (blocked || placed || rowBlocked);
          const dayMaybe = showAvailability && !holidayUuid && !dayBlocked && (maybe || rowMaybe);
          const dayClasses = classNames('droppable', styles.day, {
            [styles['row-blocked']]: dayBlocked,
            [styles['row-maybe']]: dayMaybe,
            // Raum-Kontext blendet die Drop-Zone unabhängig vom "Verfügbarkeiten"-Toggle ein.
            [styles.valid]: (showAvailability || pickedInRooms) && valid && !dayBlocked && !dayMaybe && !holidayUuid,
            [styles.dimmed]: !!dimmed,
            [styles.holiday]: !!holidayUuid,
          });

          const dayTitle =
            !holidayUuid && rowHint ? `${getDayTitle(dateString)} · ${rowHint}` : getDayTitle(dateString);

          return (
            <div
              key={weekNumber + '_' + dayIndex}
              data-date-string={dateString}
              style={{ left: `calc(100% / 7 * ${dayIndex})` }}
              className={dayClasses}
              data-day-title={dayTitle}
              data-conflict-hint={hint}
              onMouseDown={(event) => {
                // event.detail < 2: den zweiten Klick eines Doppelklicks nicht als
                // Ablegen werten (sonst landet der Doppelklick als Pick + Drop).
                if (!store.pickedCard || store.resizingCard || event.detail >= 2) return;
                // Raum-Kontext: die Zeile ist ein Raum → Drop weist der (bereits platzierten,
                // raumlosen) Karte diesen Raum zu, statt sie zeitlich neu zu platzieren. Nur in
                // den markierten Zeit-Spalten der Karte (valid) ablegbar; ausgegraute Zellen nicht.
                if (store.gridContext === 'rooms') {
                  if (valid) store.assignRoomToPickedCard(row);
                  return;
                }
                if (!holidayUuid) {
                  store.placeCard(store.pickedCard.card, dateString, {
                    x: event.pageX,
                    y: event.pageY,
                  });
                }
              }}
            ></div>
          );
        },
      )}

      {store.foreignTripsForRowWeek(indexKey)?.map((trip) => {
        let offset = dayjs(trip.from).day() - 1;
        if (offset < 0) offset = 6;
        const duration = Math.max(0, trip.durationInDays / 7);
        const borders = Math.max(0, Math.floor((offset + trip.durationInDays - 1) / 7) * 2);

        return (
          <ForeignTripCard
            key={trip.key}
            card={trip}
            left={`${(offset / 7) * 100}%`}
            width={`calc(${duration * 100}% + ${borders}px)`}
          />
        );
      })}
      {virtualCardsInWeek.map((card) => {
        return <VirtualGridCard key={card.uuid} card={card} row={row} />;
      })}
      {placedCardsInWeek.map((card, i) => {
        const durationInDays = card.durationInDays ?? 7;
        let duration = durationInDays / 7;
        if (duration < 0) duration = 0;
        let offset = dayjs(card.startDate).day() - 1;
        if (offset < 0) offset = 6;
        // Border-Px an ganzzahlige Wochen-Grenzen koppeln (vgl. getTeachingBlockCardGeometry),
        // damit die abgelegte Karte exakt zur Resize-Vorschau passt und partielle Wochen korrekt
        // ausgerichtet sind.
        const borders = Math.max(0, Math.floor((offset + durationInDays - 1) / 7) * 2);

        const gridCard =
          store.gridContext === 'classes'
            ? card.classGridCards.find((c) => c.classUuid === row.value)
            : store.gridContext === 'teachers'
              ? card.teacherGridCards.find((t) => t.teacherUuid === row.value)
              : store.gridContext === 'rooms'
                ? card.roomGridCards.find((r) => r.roomUuid === row.value)
                : null;

        // Farbe (Fach-Gradient) ist über alle Grid-Card-Typen identisch. Bei frisch
        // zugewiesenem Raum fehlt der roomGridCard noch (kommt erst nach Refetch) → als
        // Fallback die Farbe einer Klassen-/Lehrer-Card nehmen, damit die Karte nicht grau ist.
        const colorCard = gridCard?.cssGradientString
          ? gridCard
          : (card.classGridCards[0] ?? card.teacherGridCards[0] ?? card.roomGridCards[0]);

        // Darstellung „Fachfarbe für Kärtchen": die Fachfarbe ersetzt den Lehrkraft-Farbverlauf.
        // Ohne Flag oder ohne gepflegte Fachfarbe bleibt es unverändert beim Verlauf.
        const subjectColor = store.cardColorBySubject ? (card.subject?.timetableConfig?.color ?? null) : null;

        // Wahlfächer verdrängen sich nicht, sondern teilen sich die Zeile: liegt ein
        // Stapel-Layout vor, ersetzen dessen Spur und Höhe die aus der Klassenteilung
        // abgeleiteten cssTop/cssHeight.
        const coexist = store.getCoexistLayout(row.value, card.uuid);

        return (
          <GridCard
            key={card.uuid + i}
            card={card}
            onCardClick={handleCardPick}
            background={subjectColor ?? colorCard?.cssGradientString ?? ''}
            textColor={subjectColor ? getCardTextColor(subjectColor) : (colorCard?.textColor ?? '')}
            left={`${(offset / 7) * 100}%`}
            top={coexist ? `${(coexist.track * 100) / coexist.divisions}%` : (gridCard?.cssTop ?? '0%')}
            width={`calc(${duration * 100}% + ${borders}px)`}
            height={coexist ? `${100 / coexist.divisions}%` : (gridCard?.cssHeight ?? '100%')}
          />
        );
      })}
    </div>
  );
});
